import { useEffect, useMemo, useState } from "react";
import { SubmitButton } from "#/components/shared/buttons/submit-button";
import Input from "#/components/shared/input";
import ThemedSelect from "#/components/shared/themed-select";
import Typography from "#/components/shared/typography";
import {
  MediaUploadContext,
  MediaUploadContexts,
} from "#/api/mediaReference.api";
import { useUploadMediaReference } from "#/features/media/hooks/useUploadMediaReference";

type MediaUploadFormProps = {
  onComplete?: () => void;
  compact?: boolean;
};

export function MediaUploadForm({
  onComplete,
  compact = false,
}: MediaUploadFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [context, setContext] = useState<MediaUploadContext>("general");
  const { mutate, data, isPending } = useUploadMediaReference();

  const contextOptions = useMemo(
    () =>
      MediaUploadContexts.map((value) => ({
        value,
        label: value.replace(/\b\w/g, (char) => char.toUpperCase()),
      })),
    []
  );

  useEffect(() => {
    if (data) {
      setFile(null);
      setInputKey((current) => current + 1);
      onComplete?.();
    }
  }, [data, onComplete]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!file || isPending) return;
    mutate({ file, context });
  };

  const formContent = (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          key={inputKey}
          id="media-reference-file"
          label="Image file"
          type="file"
          accept="image/*"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          fullWidth
        />
        <ThemedSelect
          label="Context"
          options={contextOptions}
          initialValue={context}
          onChange={(event) =>
            setContext(event.target.value as MediaUploadContext)
          }
        />
        <Typography variant="caption" className="text-brown-600">
          {file ? file.name : "No file selected"}
        </Typography>
        <SubmitButton isDisabled={!file || isPending} isLoading={isPending}>
          Upload
        </SubmitButton>
      </form>

      {data && (
        <div className="mt-4 rounded-md border border-cream-200 bg-white p-3">
          <Typography variant="body2" className="text-brown-700">
            Upload complete
          </Typography>
          <Typography variant="caption" className="text-brown-600">
            ID: {data.id}
          </Typography>
          <Typography variant="caption" className="text-brown-600 break-all">
            CDN URL: {data.cdn_url}
          </Typography>
        </div>
      )}
    </>
  );

  if (compact) {
    return formContent;
  }

  return <div className="space-y-4">{formContent}</div>;
}
